import { defineComponent as _defineComponent } from 'vue'
import VTextfield from "@busy-human/vue-component-library/components/text-field.vue"
import VCodeInput from "@/components/code-input.vue"
import VLoader from "@/components/loader.vue"
import AuthComponent from "@busy-human/vue-component-library/components/auth.vue";
import VCheckbox from '@/components/checkmarkbox.vue'
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router';
import VPhoneInput from "@/components/phone-input.vue";
import authenImage from "@/resources/images/brook-cagle-unsplash.jpg";
import verifyImage from "@/resources/images/jason-goodman-unsplash.jpg";
import { signup, sendVerification, emailLogin, phoneLogin, getUserId } from '@/util/auth'
import { isDevEnv } from "@/util/aux-functions";
import { FirebaseError } from "@firebase/util";
import VersionLabel from "@/components/version.vue";


enum LoginStage {
    VERIFICATION = 1,
    SIGNUP = 2,
    CODE = 3,
}


export default /*#__PURE__*/_defineComponent({
  __name: 'auth',
  setup(__props, { expose }) {
  expose();

const router = useRouter();
let loading = ref(false)

const env = ref(ENVIRONMENT);
const isDevEnvironment = ref(false);
isDevEnvironment.value = isDevEnv(window);
const DEV_code = ref<string | null>(null)

let errorMsg = ref("")

const userExists = ref(false)
let enteredCode: string | null = null;

let email = ref<string | null>(null)
let phone = ref<string | null>(null)
let firstName = ref<string | null>(null)
let lastName = ref<string | null>(null)
let acceptedTerms = ref(false)

const verificationSelection = ref<"phone" | "email">("phone")
const stage = ref(LoginStage.VERIFICATION)

const createAccount = async () => {
    if(!validate()) return

    loading.value = true
    try {
        if(!acceptedTerms.value)
            throw new Error("Please Agree to the Terms and Conditions")
        await signup(firstName.value!, lastName.value!, email.value!, phone.value!, false)
        if(enteredCode != null){
            if(verificationSelection.value === 'email'){
                await emailLogin(email.value!, enteredCode);
            }
            else if(verificationSelection.value === 'phone'){
                await phoneLogin(phone.value!, enteredCode);
            }

            await finishLogin()
        }
    } catch (e) {
        console.error(e)
        if (e instanceof FirebaseError) {
            errorMsg.value = e.message;
        } else {
            errorMsg.value = "Error Creating account"
        }

    }
    loading.value = false
}




const sendVerificationCode = async () => {
    errorMsg.value = ""
    if(!validate()) return;
    const phoneNumber = phone.value;
    
    loading.value = true

    try {
        const verificationString = verificationSelection.value === 'email'
            ? email.value
            : phoneNumber;
        
        let result = await sendVerification(verificationSelection.value, verificationString!)
        DEV_code.value = result.data.code || null
        userExists.value = result.data.exists
        stage.value = LoginStage.CODE
    } catch (e) {
        if((e && typeof e === 'object' && e!.toString().includes('Cannot find this user'))){
            stage.value = LoginStage.SIGNUP
        }else{
            console.error(e);
            errorMsg.value = "Error trying to send verification code, try a different method.";
        }
    }
    loading.value = false
}

const inputCode = ref("")
watch(() => inputCode.value, val => {
    if(val.length === 5) {
        loginWithCode(val)
    }
})


const loginWithCode = async (code: string) => {
    loading.value = true
    try {
        if(!userExists.value){
            enteredCode = code
            stage.value = LoginStage.SIGNUP
        }else{
            if (verificationSelection.value == 'email') {
                await emailLogin(email.value!, code)
            } else if (verificationSelection.value == 'phone') {
                await phoneLogin(phone.value!, code)
            } else {
                console.warn("Somehow trying to login without a code destination")
            }
            errorMsg.value = ""
            await finishLogin()
        }
    }
    catch (e) {
        console.error(e);
        if(e instanceof Error) {
            errorMsg.value = e.message;
        } else {
            errorMsg.value = "Error logging in";
        }
    }

    loading.value = false
}

const finishLogin = async () => {
    router.push("/");
}

const emailRegex = /(?:[a-z0-9!#$%&'*+\/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+\/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/
const phoneRegex = /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/im

const validate = (focus?:string) => {
    errorMsg.value = ""

    if(!focus || focus == 'email')
        if((stage.value == LoginStage.SIGNUP || verificationSelection.value === 'email') && (!email.value || !emailRegex.test(email.value.toLowerCase()))){
            errorMsg.value = "Input a Valid Email Address"
            return false;
        }

    if(!focus || focus == 'phone')
        if((stage.value == LoginStage.SIGNUP || verificationSelection.value === 'phone') && (!phone.value || !phoneRegex.test(phone.value.toLowerCase()))){
            errorMsg.value = 'Input a Valid Phone Number'
            return false;
        }

    if(!focus && stage.value == LoginStage.SIGNUP){
        if(!firstName.value){
            errorMsg.value = 'Input your first name'
            return false;
        }

        if(!lastName.value){
            errorMsg.value = 'Input your last name'
            return false;
        }
    }
    return true;
}

watch([email], () => {
    if(errorMsg.value)
        validate('email');
})

watch([phone], vals => {
    if(errorMsg.value)
        validate('phone');
})

watch([verificationSelection], vals => {
    if(errorMsg.value)
        validate(verificationSelection.value)
})

const imageSource = computed(() => {
    const imagePaths = {
        1: authenImage,
        2: authenImage,
        3: verifyImage,
    }
    return imagePaths[stage.value]
});

const __returned__ = { router, loading, env, isDevEnvironment, DEV_code, errorMsg, userExists, enteredCode, email, phone, firstName, lastName, acceptedTerms, LoginStage, verificationSelection, stage, createAccount, sendVerificationCode, inputCode, loginWithCode, finishLogin, emailRegex, phoneRegex, validate, imageSource, VTextfield, VCodeInput, VLoader, AuthComponent, VCheckbox, computed, ref, watch, useRouter, VPhoneInput, authenImage, verifyImage, signup, sendVerification, emailLogin, phoneLogin, getUserId, isDevEnv, FirebaseError, VersionLabel }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})